import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import AdmicityTable from '../../../shared-components/AdmicityTable';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CategoryPicker from './CategoryPicker';
import useNotification from '../../../utility/hooks/useNotification';
import {formatDate} from '../../../utility/dateUtil';
import {FORM_TEMPLATE_TYPE_LABELS} from '../../../constants/FormTemplateType';
import {
    useChangeSharedFormTemplateCategoryMutation,
    useGetFormTemplateCategoriesQuery,
    useGetSharedFormTemplatesQuery,
    useSetSharedFormTemplateHiddenMutation
} from '../../../api/services/formTemplateLibraryService';

const TemplateList = ({hidden}) => {
    const navigate = useNavigate();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const {data: templates = [], isFetching} = useGetSharedFormTemplatesQuery(hidden);
    const {data: categories = []} = useGetFormTemplateCategoriesQuery();
    const [setTemplateHidden] = useSetSharedFormTemplateHiddenMutation();
    const [changeCategory] = useChangeSharedFormTemplateCategoryMutation();

    const [selected, setSelected] = useState([]);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [categoryDialogFor, setCategoryDialogFor] = useState(null);
    const [chosenCategory, setChosenCategory] = useState(null);

    const rows = useMemo(() => {
        const mapped = templates.map(template => ({
            ...template,
            typeLabel: FORM_TEMPLATE_TYPE_LABELS[template.type] ?? template.type,
            categoryName: template.parentCategory ? template.parentCategory : template.category,
            subcategoryName: template.parentCategory ? template.category : ''
        }));

        const term = search.trim().toLowerCase();

        return mapped.filter(row => {
            const matchesName = term.length === 0 || row.name.toLowerCase().includes(term);
            const matchesCategory = categoryFilter === '' || row.categoryName === categoryFilter;

            return matchesName && matchesCategory;
        });
    }, [templates, search, categoryFilter]);

    const categoryNames = useMemo(() => {
        const names = categories.filter(category => !category.parentId).map(category => category.name);

        return [...new Set(names)].sort();
    }, [categories]);

    const columns = useMemo(() => {
        const base = [
            {field: 'name', headerName: 'Name', flex: 2, sortable: false},
            {field: 'typeLabel', headerName: 'Form Type', flex: 1, sortable: false},
            {field: 'categoryName', headerName: 'Category', flex: 1, sortable: false},
            {field: 'subcategoryName', headerName: 'Subcategory', flex: 1, sortable: false}
        ];

        if (hidden) {
            return [
                ...base,
                {
                    field: 'hiddenAt',
                    headerName: 'Made Inactive',
                    flex: 1,
                    sortable: false,
                    valueGetter: (params) => params.row?.hiddenAt ? formatDate(params.row.hiddenAt) : ''
                },
                {field: 'hiddenBy', headerName: 'Made Inactive By', flex: 1, sortable: false}
            ];
        }

        return [
            ...base,
            {
                field: 'createdAt',
                headerName: 'Created',
                flex: 1,
                sortable: false,
                valueGetter: (params) => params.row?.createdAt ? formatDate(params.row.createdAt) : ''
            },
            {field: 'createdBy', headerName: 'Created By', flex: 1, sortable: false}
        ];
    }, [hidden]);

    const openCategoryDialog = (templateIds) => {
        const first = rows.find(row => row.id === templateIds[0]);
        setChosenCategory(first?.categoryId ?? null);
        setCategoryDialogFor(templateIds);
    };

    const applyCategory = async () => {
        const result = await changeCategory({
            templateIds: categoryDialogFor,
            categoryId: chosenCategory
        });

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to change the category');
            return;
        }

        showSuccessNotification(categoryDialogFor.length === 1
            ? 'The category has been changed'
            : `The category has been changed for ${categoryDialogFor.length} form templates`);

        setCategoryDialogFor(null);
        setSelected([]);
    };

    const changeHidden = async (row, nextHidden) => {
        const result = await setTemplateHidden({templateId: row.id, hidden: nextHidden});

        if (result.error) {
            showErrorNotification(result.error.message ?? 'Failed to change the form template');
            return;
        }

        showSuccessNotification(nextHidden
            ? `${row.name} is now inactive`
            : `${row.name} is active again`);
    };

    const rowActions = hidden
        ? [
            {label: 'Edit template', action: row => navigate(`/management/templates/edit/${row.id}`)},
            {label: 'Make active', action: row => changeHidden(row, false)}
        ]
        : [
            {label: 'Edit template', action: row => navigate(`/management/templates/edit/${row.id}`)},
            {label: 'Change category', action: row => openCategoryDialog([row.id])},
            {label: 'Make inactive', action: row => changeHidden(row, true)}
        ];

    return (
        <>
            <Stack direction={{xs: 'column', sm: 'row'}} gap={2} sx={{mb: 2, maxWidth: 640}}>
                <TextField
                    fullWidth
                    size="small"
                    label="Search by name"
                    data-testid="template-search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
                <TextField
                    select
                    fullWidth
                    size="small"
                    label="Category"
                    data-testid="template-category-filter"
                    value={categoryFilter}
                    onChange={(event) => setCategoryFilter(event.target.value)}
                >
                    <MenuItem value="">Every category</MenuItem>
                    {categoryNames.map(name => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                </TextField>
            </Stack>

            {!hidden && selected.length > 0 && (
                <Stack direction="row" gap={2} sx={{mb: 2}}>
                    <Button
                        variant="outlined"
                        data-testid="change-category-for-selected"
                        onClick={() => openCategoryDialog(selected)}
                    >
                        Change category for {selected.length} selected
                    </Button>
                </Stack>
            )}

            <AdmicityTable
                isLoading={isFetching}
                data={rows}
                columns={columns}
                totalItemCount={rows.length}
                pageSize={25}
                hideFooterPagination={false}
                checkboxSelection={!hidden}
                handleRowSelection={setSelected}
                rowSelectionModel={selected}
                noRowsOverlay={{
                    icon: <DescriptionOutlinedIcon/>,
                    text: hidden ? 'No form templates have been made inactive' : 'No form templates are in the library'
                }}
                rowActions={rowActions}
            />

            <Dialog open={Boolean(categoryDialogFor)} onClose={() => setCategoryDialogFor(null)} fullWidth>
                <DialogTitle>Change category</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <CategoryPicker
                        categories={categories}
                        value={chosenCategory}
                        onChange={setChosenCategory}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCategoryDialogFor(null)}>Cancel</Button>
                    <Button variant="contained" data-testid="apply-category" onClick={applyCategory}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

TemplateList.propTypes = {
    hidden: PropTypes.bool.isRequired
};

export default TemplateList;
